import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { getCourseTemplate } from "../api/courses";
import {
	teacherGetRun,
	teacherCreateUnit,
	teacherUpdateUnit,
	teacherDeleteUnit,
	teacherCreateModule,
	teacherUpdateModule,
	teacherDeleteModule,
	teacherCreateContent,
	teacherUpdateContent,
	teacherDeleteContent,
} from "../../api/academic";
import { getFileIconClass } from "../../utils/fileIconUtils";
import RichTextEditor from "../components/RichTextEditor";

function getErrorMessage(e) {
	return e?.response?.data?.error || e?.message || "Ha ocurrido un error.";
}

const CATEGORY_LABELS = {
	material: "Material",
	clases: "Clases",
	evaluaciones: "Evaluaciones",
};
const CATEGORIES = ["material", "clases", "evaluaciones"];

export default function CourseStructurePage() {
	const { templateId } = useParams();
	const [course, setCourse] = useState(null);
	const [run, setRun] = useState(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const [expandedUnits, setExpandedUnits] = useState({});
	const [openCategory, setOpenCategory] = useState(null);
	const [modal, setModal] = useState(null);
	const [saving, setSaving] = useState(false);
	const [actionError, setActionError] = useState(null);

	const runId = course?.default_run?.id ?? null;

	const loadCourse = useCallback(async () => {
		if (!templateId) return;
		setLoading(true);
		setError(null);
		try {
			const data = await getCourseTemplate(templateId);
			setCourse(data);
		} catch (e) {
			setError(
				e?.response?.data?.detail || "No se pudo cargar el curso.",
			);
		} finally {
			setLoading(false);
		}
	}, [templateId]);

	const loadRun = useCallback(async () => {
		if (!runId) return;
		setLoading(true);
		setError(null);
		try {
			const data = await teacherGetRun(runId);
			setRun(data);
		} catch (e) {
			setError(getErrorMessage(e));
		} finally {
			setLoading(false);
		}
	}, [runId]);

	useEffect(() => {
		loadCourse();
	}, [loadCourse]);

	useEffect(() => {
		if (!runId) {
			setRun(null);
			return;
		}
		loadRun();
	}, [loadRun, runId]);

	useEffect(() => {
		if (!modal) return;
		setActionError(null);
		const onKeyDown = (e) => {
			if (e.key === "Escape") setModal(null);
		};
		window.addEventListener("keydown", onKeyDown);
		return () => window.removeEventListener("keydown", onKeyDown);
	}, [modal]);

	const toggleUnit = (id) => {
		setExpandedUnits((prev) => ({ ...prev, [id]: !prev[id] }));
	};

	const toggleCategory = (moduleId, cat) => {
		setOpenCategory((prev) =>
			prev?.moduleId === moduleId && prev?.cat === cat
				? null
				: { moduleId, cat },
		);
	};

	const clearActionError = () => setActionError(null);

	const handleAddUnit = async (title) => {
		if (!title?.trim() || !runId) return;
		setSaving(true);
		setActionError(null);
		try {
			await teacherCreateUnit(runId, { title: title.trim() });
			loadRun();
			setModal(null);
		} catch (e) {
			setActionError(getErrorMessage(e));
		} finally {
			setSaving(false);
		}
	};

	const handleEditUnit = async (unitId, title) => {
		if (!title?.trim()) return;
		setSaving(true);
		setActionError(null);
		try {
			await teacherUpdateUnit(unitId, { title: title.trim() });
			loadRun();
			setModal(null);
		} catch (e) {
			setActionError(getErrorMessage(e));
		} finally {
			setSaving(false);
		}
	};

	const handleDeleteUnit = async (unitId) => {
		if (!window.confirm("¿Eliminar esta unidad y todo su contenido?"))
			return;
		setSaving(true);
		setActionError(null);
		try {
			await teacherDeleteUnit(unitId);
			loadRun();
			setModal(null);
		} catch (e) {
			setActionError(getErrorMessage(e));
		} finally {
			setSaving(false);
		}
	};

	const handleAddWeek = async (unitId, title) => {
		setSaving(true);
		setActionError(null);
		try {
			await teacherCreateModule(unitId, {
				title: title?.trim() || "Nueva semana",
			});
			loadRun();
			setModal(null);
		} catch (e) {
			setActionError(getErrorMessage(e));
		} finally {
			setSaving(false);
		}
	};

	const handleEditWeek = async (moduleId, title) => {
		if (!title?.trim()) return;
		setSaving(true);
		setActionError(null);
		try {
			await teacherUpdateModule(moduleId, { title: title.trim() });
			loadRun();
			setModal(null);
		} catch (e) {
			setActionError(getErrorMessage(e));
		} finally {
			setSaving(false);
		}
	};

	const handleDeleteWeek = async (moduleId) => {
		if (!window.confirm("¿Eliminar esta semana y sus recursos?")) return;
		setSaving(true);
		setActionError(null);
		try {
			await teacherDeleteModule(moduleId);
			loadRun();
			setModal(null);
		} catch (e) {
			setActionError(getErrorMessage(e));
		} finally {
			setSaving(false);
		}
	};

	const handleAddResource = async (moduleId, payload, file) => {
		setSaving(true);
		setActionError(null);
		try {
			await teacherCreateContent(moduleId, payload, file);
			loadRun();
			setModal(null);
		} catch (e) {
			setActionError(getErrorMessage(e));
		} finally {
			setSaving(false);
		}
	};

	const handleEditResource = async (contentId, payload, file) => {
		setSaving(true);
		setActionError(null);
		try {
			await teacherUpdateContent(contentId, payload, file);
			loadRun();
			setModal(null);
		} catch (e) {
			setActionError(getErrorMessage(e));
		} finally {
			setSaving(false);
		}
	};

	const handleDeleteResource = async (contentId) => {
		if (!window.confirm("¿Eliminar este recurso?")) return;
		setSaving(true);
		setActionError(null);
		try {
			await teacherDeleteContent(contentId);
			loadRun();
			setModal(null);
		} catch (e) {
			setActionError(getErrorMessage(e));
		} finally {
			setSaving(false);
		}
	};

	if (loading && !course && !run) {
		return (
			<div className="container-fluid">
				<p className="text-muted">Cargando...</p>
			</div>
		);
	}

	if (error && !course) {
		return (
			<div className="container-fluid">
				<div className="alert alert-danger">{error}</div>
				<Link
					to="/backoffice/courses"
					className="btn btn-outline-secondary"
				>
					Volver a Cursos
				</Link>
			</div>
		);
	}

	if (!runId) {
		return (
			<div className="container-fluid">
				<div className="alert alert-warning">
					Este curso aún no tiene edición (fechas y docente). Guarda
					el curso con fecha de inicio, fecha de término y docente
					asignado, luego usa &quot;Inicializar estructura de la
					edición&quot; en la edición del curso.
				</div>
				<Link
					to="/backoffice/courses"
					className="btn btn-outline-secondary"
				>
					Volver a Cursos
				</Link>
			</div>
		);
	}

	if (loading && !run) {
		return (
			<div className="container-fluid">
				<p className="text-muted">Cargando contenido del curso...</p>
			</div>
		);
	}

	if (error && run === null) {
		return (
			<div className="container-fluid">
				<div className="alert alert-danger">{error}</div>
				<Link
					to="/backoffice/courses"
					className="btn btn-outline-secondary"
				>
					Volver a Cursos
				</Link>
			</div>
		);
	}

	const courseName = course?.name || run?.name || run?.code || "Curso";

	return (
		<div className="container-fluid">
			{actionError && (
				<div
					className="alert alert-danger d-flex align-items-center justify-content-between mb-3"
					role="alert"
				>
					<span>{actionError}</span>
					<button
						type="button"
						className="btn-close"
						onClick={clearActionError}
						aria-label="Cerrar"
					/>
				</div>
			)}

			<nav
				className="mb-3"
				style={{ fontSize: "0.9rem" }}
				aria-label="Miga de pan"
			>
				<Link to="/backoffice/courses">Cursos</Link>
				<span className="mx-2">›</span>
				<Link to="/backoffice/courses">
					{course?.code || courseName}
				</Link>
				<span className="mx-2">›</span>
				<span>Contenido del curso</span>
			</nav>

			<div className="card shadow-sm mb-4">
				<div className="card-body">
					<h1 className="h4 mb-2">
						{run?.code && run?.name
							? `${run.code} – ${courseName}`
							: courseName}
					</h1>
					<p className="text-muted small mb-0">
						Unidades, módulos y materiales que verán los estudiantes
						en el aula virtual. Los cambios se guardan en la base de
						datos y se reflejan de inmediato en el portal del
						estudiante.
					</p>
					{run?.start_date && run?.end_date && (
						<p className="text-muted small mt-2 mb-0">
							Edición:{" "}
							{new Date(run.start_date).toLocaleDateString(
								"es-CL",
							)}{" "}
							–{" "}
							{new Date(run.end_date).toLocaleDateString("es-CL")}
						</p>
					)}
				</div>
			</div>

			<div className="card shadow-sm">
				<div className="card-header d-flex justify-content-between align-items-center flex-wrap gap-2">
					<h2 className="h6 mb-0">Unidades y contenido</h2>
					<button
						type="button"
						className="btn btn-primary btn-sm"
						onClick={() => setModal({ type: "addUnit" })}
					>
						+ Agregar unidad
					</button>
				</div>
				<div className="card-body">
					{(run?.units || []).map((unit) => (
						<div
							key={unit.id}
							className="border rounded-2 mb-3 overflow-hidden"
						>
							<div
								className="p-3 bg-light d-flex align-items-center justify-content-between flex-wrap gap-2 cursor-pointer"
								onClick={() => toggleUnit(unit.id)}
								onKeyDown={(e) =>
									e.key === "Enter" && toggleUnit(unit.id)
								}
								role="button"
								tabIndex={0}
								aria-expanded={expandedUnits[unit.id] !== false}
							>
								<h3 className="h6 mb-0 d-flex align-items-center gap-2">
									{unit.title}
									<i
										className={`ri-arrow-down-s-line ${expandedUnits[unit.id] !== false ? "rotate-180" : ""}`}
										style={{ transition: "transform 0.2s" }}
										aria-hidden
									/>
								</h3>
								<div
									className="d-flex gap-1"
									onClick={(e) => e.stopPropagation()}
								>
									<button
										type="button"
										className="btn btn-outline-secondary btn-sm"
										onClick={() =>
											setModal({ type: "editUnit", unit })
										}
									>
										Editar
									</button>
									<button
										type="button"
										className="btn btn-outline-primary btn-sm"
										onClick={() =>
											setModal({ type: "addWeek", unit })
										}
									>
										+ Semana
									</button>
									<button
										type="button"
										className="btn btn-outline-danger btn-sm"
										onClick={() =>
											handleDeleteUnit(unit.id)
										}
									>
										Eliminar
									</button>
								</div>
							</div>
							{expandedUnits[unit.id] !== false && (
								<div className="p-3">
									{(unit.modules || []).map((mod) => (
										<div key={mod.id} className="mb-4">
											<div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-2">
												<strong>{mod.title}</strong>
												<div className="d-flex gap-1">
													<button
														type="button"
														className="btn btn-link btn-sm p-0"
														onClick={() =>
															setModal({
																type: "editWeek",
																module: mod,
															})
														}
													>
														Editar
													</button>
													<button
														type="button"
														className="btn btn-link btn-sm p-0 text-danger"
														onClick={() =>
															handleDeleteWeek(
																mod.id,
															)
														}
													>
														Eliminar
													</button>
												</div>
											</div>
											<div className="d-flex flex-wrap gap-2">
												{CATEGORIES.map((cat) => {
													const isOpen =
														openCategory?.moduleId ===
														mod.id &&
														openCategory?.cat ===
														cat;
													const items =
														(mod.contents &&
															mod.contents[
															cat
															]) ||
														[];
													return (
														<div
															key={cat}
															className="flex-grow-1"
															style={{
																minWidth: 140,
															}}
														>
															<button
																type="button"
																className={`btn btn-sm w-100 text-start ${isOpen ? "btn-primary" : "btn-outline-secondary"}`}
																onClick={() =>
																	toggleCategory(
																		mod.id,
																		cat,
																	)
																}
															>
																{
																	CATEGORY_LABELS[
																	cat
																	]
																}
																<span className="badge bg-secondary ms-1">
																	{
																		items.length
																	}
																</span>
															</button>
															{isOpen && (
																<div className="border rounded mt-2 p-2 bg-white">
																	<div className="d-flex justify-content-between align-items-center mb-2">
																		<span className="small fw-bold text-muted">
																			{
																				CATEGORY_LABELS[
																				cat
																				]
																			}
																		</span>
																		<button
																			type="button"
																			className="btn btn-link btn-sm p-0"
																			onClick={() =>
																				setModal(
																					{
																						type: "addResource",
																						module: mod,
																						category:
																							cat,
																					},
																				)
																			}
																		>
																			+
																			Añadir
																		</button>
																	</div>
																	{items.length ===
																		0 ? (
																		<p className="text-muted small mb-0">
																			Sin
																			contenido.
																		</p>
																	) : (
																		<ul className="list-unstyled mb-0 small">
																			{items.map(
																				(
																					c,
																				) => {
																					const iconClass =
																						c.file
																							? getFileIconClass(
																								c.file_name ||
																								c.file,
																							)
																							: c.url
																								? "ri-links-line"
																								: "ri-file-line";
																					const label =
																						c.title ||
																						(c.file
																							? "Archivo"
																							: "Recurso");
																					return (
																						<li
																							key={
																								c.id
																							}
																							className="d-flex align-items-center gap-2 py-1 flex-wrap"
																						>
																							<i
																								className={`${iconClass} opacity-75`}
																								aria-hidden
																							/>
																							<span>
																								{
																									label
																								}
																							</span>
																							{c.url && (
																								<a
																									href={
																										c.url
																									}
																									target="_blank"
																									rel="noreferrer"
																									className="small"
																								>
																									Ver
																								</a>
																							)}
																							{c.file && (
																								<a
																									href={
																										c.file
																									}
																									target="_blank"
																									rel="noreferrer"
																									download={
																										c.file_name
																									}
																									className="small"
																								>
																									Descargar
																								</a>
																							)}
																							<button
																								type="button"
																								className="btn btn-link btn-sm p-0"
																								onClick={() =>
																									setModal(
																										{
																											type: "editResource",
																											content:
																												c,
																											module: mod,
																										},
																									)
																								}
																							>
																								Editar
																							</button>
																							<button
																								type="button"
																								className="btn btn-link btn-sm p-0 text-danger"
																								onClick={() =>
																									handleDeleteResource(
																										c.id,
																									)
																								}
																							>
																								×
																							</button>
																						</li>
																					);
																				},
																			)}
																		</ul>
																	)}
																</div>
															)}
														</div>
													);
												})}
											</div>
										</div>
									))}
								</div>
							)}
						</div>
					))}

					{(!run?.units || run.units.length === 0) && (
						<div className="text-muted py-4 text-center">
							No hay unidades. Usa &quot;Agregar unidad&quot; para
							crear la primera, o &quot;Inicializar estructura de
							la edición&quot; en la pantalla de edición del
							curso.
						</div>
					)}
				</div>
			</div>

			{/* Modals */}
			{modal?.type === "addUnit" && (
				<ModalForm
					title="Nueva unidad"
					initial={{ title: "" }}
					fields={[
						{ name: "title", label: "Título", required: true },
					]}
					onSubmit={(data) => handleAddUnit(data.title)}
					onClose={() => setModal(null)}
					saving={saving}
				/>
			)}
			{modal?.type === "editUnit" && (
				<ModalForm
					title="Editar unidad"
					initial={{ title: modal.unit?.title || "" }}
					fields={[
						{ name: "title", label: "Título", required: true },
					]}
					onSubmit={(data) =>
						handleEditUnit(modal.unit.id, data.title)
					}
					onClose={() => setModal(null)}
					saving={saving}
				/>
			)}
			{modal?.type === "addWeek" && (
				<ModalForm
					title="Nueva semana / módulo"
					initial={{ title: "" }}
					fields={[
						{
							name: "title",
							label: "Título (ej: Semana 1)",
							required: false,
						},
					]}
					onSubmit={(data) =>
						handleAddWeek(modal.unit?.id, data.title)
					}
					onClose={() => setModal(null)}
					saving={saving}
				/>
			)}
			{modal?.type === "editWeek" && (
				<ModalForm
					title="Editar semana / módulo"
					initial={{ title: modal.module?.title || "" }}
					fields={[
						{ name: "title", label: "Título", required: true },
					]}
					onSubmit={(data) =>
						handleEditWeek(modal.module?.id, data.title)
					}
					onClose={() => setModal(null)}
					saving={saving}
				/>
			)}
			{modal?.type === "addResource" && (
				<ResourceForm
					title="Nuevo recurso"
					category={modal.category}
					onSubmit={(payload, file) =>
						handleAddResource(modal.module?.id, payload, file)
					}
					onClose={() => setModal(null)}
					saving={saving}
				/>
			)}
			{modal?.type === "editResource" && (
				<ResourceForm
					title="Editar recurso"
					initial={modal.content}
					onSubmit={(payload, file) =>
						handleEditResource(modal.content?.id, payload, file)
					}
					onClose={() => setModal(null)}
					saving={saving}
				/>
			)}
		</div>
	);
}

function ModalForm({ title, initial, fields, onSubmit, onClose, saving }) {
	const [form, setForm] = useState(initial || {});
	const [fieldErrors, setFieldErrors] = useState({});

	const handleSubmit = (e) => {
		e.preventDefault();
		if (saving) return;
		const err = {};
		fields.forEach((f) => {
			if (f.required && !(form[f.name] ?? "").trim()) {
				err[f.name] = "Este campo es obligatorio.";
			}
		});
		setFieldErrors(err);
		if (Object.keys(err).length > 0) return;
		onSubmit(form);
	};

	const handleBackdrop = (e) => {
		if (e.target === e.currentTarget) onClose();
	};

	const handleChange = (name, value) => {
		setForm((p) => ({ ...p, [name]: value }));
		if (fieldErrors[name])
			setFieldErrors((p) => ({ ...p, [name]: undefined }));
	};

	return (
		<div
			className="modal show d-block"
			style={{ background: "rgba(0,0,0,0.5)" }}
			tabIndex={-1}
			onClick={handleBackdrop}
			role="dialog"
			aria-modal="true"
			aria-labelledby="backoffice-modal-title"
		>
			<div
				className="modal-dialog modal-dialog-centered"
				onClick={(e) => e.stopPropagation()}
			>
				<div className="modal-content">
					<div className="modal-header">
						<h5 id="backoffice-modal-title" className="modal-title">
							{title}
						</h5>
						<button
							type="button"
							className="btn-close"
							onClick={onClose}
							aria-label="Cerrar"
						/>
					</div>
					<form onSubmit={handleSubmit}>
						<div className="modal-body">
							{fields.map((f) => (
								<div key={f.name} className="mb-3">
									<label
										className="form-label"
										htmlFor={`backoffice-field-${f.name}`}
									>
										{f.label}
									</label>
									<input
										id={`backoffice-field-${f.name}`}
										type="text"
										className={`form-control ${fieldErrors[f.name] ? "is-invalid" : ""}`}
										value={form[f.name] ?? ""}
										placeholder="Ingresa un valor" onChange={(e) => handleChange(f.name, e.target.value)
										}
										required={f.required}
										autoComplete="off"
										aria-invalid={!!fieldErrors[f.name]}
									/>
									{fieldErrors[f.name] && (
										<div className="invalid-feedback">
											{fieldErrors[f.name]}
										</div>
									)}
								</div>
							))}
						</div>
						<div className="modal-footer">
							<button
								type="button"
								className="btn btn-outline-secondary"
								onClick={onClose}
								disabled={saving}
							>
								Cancelar
							</button>
							<button
								type="submit"
								className="btn btn-primary"
								disabled={saving}
							>
								{saving ? "Guardando..." : "Guardar"}
							</button>
						</div>
					</form>
				</div>
			</div>
		</div>
	);
}

function FileDropZone({
	file,
	existingName,
	existingUrl,
	accept,
	onFileChange,
}) {
	const inputRef = useRef(null);
	const [dragging, setDragging] = useState(false);
	const [popupOpen, setPopupOpen] = useState(false);

	const hasStaged = !!file;
	const hasExisting = !!existingName && !hasStaged;
	const showDropZone = !hasStaged && !hasExisting;

	const handleDragOver = (e) => {
		e.preventDefault();
		setDragging(true);
	};
	const handleDragLeave = () => setDragging(false);
	const handleDrop = (e) => {
		e.preventDefault();
		setDragging(false);
		const dropped = e.dataTransfer.files?.[0];
		if (dropped) onFileChange(dropped);
	};
	const handleInputChange = (e) => {
		if (e.target.files?.[0]) onFileChange(e.target.files[0]);
	};

	if (hasStaged || hasExisting) {
		const displayName = file ? file.name : existingName;
		const sizeLabel = file
			? file.size >= 1048576
				? `${(file.size / 1048576).toFixed(1)} MB`
				: `${(file.size / 1024).toFixed(0)} KB`
			: null;
		const iconClass = getFileIconClass
			? getFileIconClass(displayName)
			: "ri-file-line";

		return (
			<div className="resource-file-manager">
				<div className="resource-file-grid">
					<button
						type="button"
						className="resource-file-card"
						onClick={() => setPopupOpen(true)}
						title="Haz clic para ver opciones"
					>
						<i
							className={`${iconClass} resource-file-card-icon`}
							aria-hidden
						/>
						<span className="resource-file-card-name">
							{displayName}
						</span>
						{sizeLabel && (
							<span className="resource-file-card-size">
								{sizeLabel}
							</span>
						)}
					</button>
				</div>
				{popupOpen && (
					<div
						className="resource-file-popup-backdrop"
						onClick={() => setPopupOpen(false)}
					>
						<div
							className="resource-file-popup"
							onClick={(e) => e.stopPropagation()}
						>
							<div className="resource-file-popup-header">
								<span className="resource-file-popup-title">
									{displayName}
								</span>
								<button
									type="button"
									className="btn-close"
									onClick={() => setPopupOpen(false)}
									aria-label="Cerrar"
								/>
							</div>
							<div className="resource-file-popup-actions">
								{existingUrl && hasExisting && (
									<a
										href={existingUrl}
										target="_blank"
										rel="noopener noreferrer"
										className="btn btn-sm btn-outline-secondary"
									>
										<i
											className="ri-download-2-line"
											aria-hidden
										/>{" "}
										Descargar
									</a>
								)}
								<button
									type="button"
									className="btn btn-sm btn-outline-primary"
									onClick={() => {
										setPopupOpen(false);
										inputRef.current?.click();
									}}
								>
									<i
										className="ri-upload-2-line"
										aria-hidden
									/>{" "}
									Reemplazar
								</button>
								<button
									type="button"
									className="btn btn-sm btn-outline-danger"
									onClick={() => {
										onFileChange(null);
										setPopupOpen(false);
									}}
								>
									<i
										className="ri-delete-bin-line"
										aria-hidden
									/>{" "}
									Quitar
								</button>
							</div>
							{sizeLabel && (
								<p className="resource-file-popup-meta">
									Tamaño: {sizeLabel}
								</p>
							)}
						</div>
					</div>
				)}
				<input
					ref={inputRef}
					type="file"
					accept={accept}
					className="d-none"
					onChange={handleInputChange}
				/>
			</div>
		);
	}

	if (showDropZone) {
		return (
			<div
				className={`resource-dropzone ${dragging ? "is-dragging" : ""}`}
				onDragOver={handleDragOver}
				onDragLeave={handleDragLeave}
				onDrop={handleDrop}
				onClick={() => inputRef.current?.click()}
				role="button"
				tabIndex={0}
				onKeyDown={(e) =>
					e.key === "Enter" && inputRef.current?.click()
				}
			>
				<i
					className="ri-upload-cloud-2-line resource-dropzone-icon"
					aria-hidden
				/>
				<p className="resource-dropzone-text">
					Arrastra tu archivo aquí o{" "}
					<strong>haz clic para seleccionar</strong>
				</p>
				<input
					ref={inputRef}
					type="file"
					accept={accept}
					className="d-none"
					onChange={handleInputChange}
				/>
			</div>
		);
	}

	return null;
}

function ResourceForm({ title, category, initial, onSubmit, onClose, saving }) {
	const [titleVal, setTitleVal] = useState(initial?.title ?? "");
	const [contentType, setContentType] = useState(
		initial?.content_type ?? "file",
	);
	const [urlVal, setUrlVal] = useState(initial?.url ?? "");
	const [textVal, setTextVal] = useState(initial?.text_content ?? "");
	const [file, setFile] = useState(null);
	const [titleError, setTitleError] = useState("");

	const showFileUpload = contentType === "file" || contentType === "image";
	const showUrl = contentType === "text" || contentType === "video";
	const showText = contentType === "text";

	const fileAccept =
		contentType === "image"
			? ".jpg,.jpeg,.png,.gif,.webp"
			: ".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.odt,.ods,.odp,.csv,.rtf,.zip,.rar";

	const urlLabel =
		contentType === "video"
			? "URL del video (YouTube, Vimeo, etc.)"
			: "URL / Enlace";

	const handleSubmit = (e) => {
		e.preventDefault();
		if (saving) return;
		if (!titleVal.trim()) {
			setTitleError("El título del recurso es obligatorio.");
			return;
		}
		setTitleError("");
		const payload = {
			title: titleVal.trim(),
			category: category || initial?.category || "material",
			content_type: contentType,
			url: urlVal.trim() || undefined,
			text_content: textVal.trim() || undefined,
		};
		onSubmit(payload, file || undefined);
	};

	const handleBackdrop = (e) => {
		if (e.target === e.currentTarget) onClose();
	};

	const effectiveCategory = category || initial?.category || "material";

	return (
		<div
			className="modal show d-block"
			style={{ background: "rgba(0,0,0,0.5)" }}
			tabIndex={-1}
			onClick={handleBackdrop}
			role="dialog"
			aria-modal="true"
			aria-labelledby="backoffice-resource-modal-title"
		>
			<div
				className="modal-dialog modal-dialog-centered"
				onClick={(e) => e.stopPropagation()}
			>
				<div className="modal-content">
					<div className="modal-header">
						<h5
							id="backoffice-resource-modal-title"
							className="modal-title"
						>
							{title}
						</h5>
						<button
							type="button"
							className="btn-close"
							onClick={onClose}
							aria-label="Cerrar"
						/>
					</div>
					<form onSubmit={handleSubmit}>
						<div className="modal-body">
							<div className="border rounded p-3 mb-3 bg-light-subtle">
								<div className="d-flex justify-content-between align-items-center mb-2">
									<h6 className="mb-0">General</h6>
									<span className="badge text-bg-light border">
										{CATEGORY_LABELS[effectiveCategory] ||
											"Material"}
									</span>
								</div>
								<div className="mb-3">
									<label
										className="form-label"
										htmlFor="resource-title"
									>
										Nombre del recurso
									</label>
									<input
										id="resource-title"
										type="text"
										className={`form-control ${titleError ? "is-invalid" : ""}`}
										value={titleVal} onChange={(e) => {
											setTitleVal(e.target.value);
											if (titleError) setTitleError("");
										}}
										placeholder="Ej: Guía semana 3"
										autoComplete="off"
										aria-invalid={!!titleError}
									/>
									{titleError && (
										<div className="invalid-feedback">
											{titleError}
										</div>
									)}
								</div>
								<div className="mb-0">
									<label
										className="form-label"
										htmlFor="resource-type"
									>
										Tipo de recurso
									</label>
									<select
										id="resource-type"
										className="form-select"
										value={contentType}
										onChange={(e) =>
											setContentType(e.target.value)
										}
									>
										<option value="file">
											Archivo (PDF, Word, etc.)
										</option>
										<option value="image">Imagen</option>
										<option value="video">Video</option>
										<option value="text">
											Texto / Enlace
										</option>
									</select>
								</div>
							</div>

							{showFileUpload && (
								<div className="border rounded p-3 mb-3">
									<h6 className="mb-3">
										{contentType === "image"
											? "Imagen"
											: "Archivo"}
									</h6>
									<FileDropZone
										file={file}
										existingName={
											initial?.file_name || null
										}
										existingUrl={initial?.file || null}
										accept={fileAccept}
										onFileChange={setFile}
									/>
								</div>
							)}

							{showUrl && (
								<div className="border rounded p-3 mb-3">
									<h6 className="mb-3">{urlLabel}</h6>
									<input
										id="resource-url"
										type="url"
										className="form-control"
										value={urlVal}
										onChange={(e) => setUrlVal(e.target.value)}
										placeholder={
											contentType === "video"
												? "https://www.youtube.com/watch?v=..."
												: "https://..."
										}
									/>
								</div>
							)}

							{showText && (
								<div className="border rounded p-3">
									<h6 className="mb-3">
										Descripción (opcional)
									</h6>
									<RichTextEditor
										value={textVal}
										onChange={setTextVal}
										placeholder="Escribe una descripción o instrucciones..."
									/>
								</div>
							)}
						</div>
						<div className="modal-footer">
							<button
								type="button"
								className="btn btn-outline-secondary"
								onClick={onClose}
								disabled={saving}
							>
								Cancelar
							</button>
							<button
								type="submit"
								className="btn btn-primary"
								disabled={saving}
							>
								{saving ? "Guardando..." : "Guardar"}
							</button>
						</div>
					</form>
				</div>
			</div>
		</div>
	);
}
